import type { AddressInfo, AssigneeDetails, MediaType, TagData } from 'src/helper/shared-types';
import type { GroupedDataMetaItem } from 'src/redux/list-view/types';
import type {
  NormalizedResidentAvailabilityType,
  WorkOrderDataObj,
} from 'src/redux/work-order/types';

/**
 * Service request property
 */
export type ServiceRequestProperty = {
  property_id: string;
  address: AddressInfo;
  /** Legacy address values the edit form pre-fills from. */
  editable_address?: AddressInfo;
  /** True while the shown address comes from a building Google has verified. */
  address_verified?: boolean;
  name: string;
  state: {
    label: string;
    value: string;
  };
  location: {
    latitude: number;
    longitude: number;
  };
  residents: Array<ResidentInfoType>;
  access_information: {
    access_method: { value: string; label: string };
    access_code: string;
    access_note: string;
    pets?: { selected: { label: string; value: string }[]; other_detail?: string | null } | null;
    additional_notes?: string | null;
  };
  access_methods: Array<{
    label: string;
    value: string;
  }>;
};
export type ResidentAvailabilityType = {
  date: string;
  timings: Array<{ [key: string]: boolean }>;
  isSaved: boolean;
};

export type ResidentAvailabilityResponseType = {
  date: string;
  timings: Array<string>;
};

export type ResidentInfoType = {
  resident_id: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  is_primary: boolean;
};

/**
 * Service request type
 */
export type ServiceRequestType = {
  label: string;
  value: string;
};

export type StatusType = {
  label: string;
  value: string;
  color_class: string;
};

/**
 * Service request imported from
 */
export type ServiceRequestImportedFrom = {
  label: string;
  value: string;
};

/**
 * Service request priority
 */
export type ServiceRequestPriority = {
  value: string;
  label: string;
};

/**
 * Service request categories
 */
export type ServiceRequestCategory = {
  problem_category: {
    label: string;
    value: string;
  };
  problem_sub_category: {
    label: string;
    value: string;
  };
};

/**
 * Service request description history type
 */

export type DescriptionHistoryType = {
  service_request_description_id: string;
  description: string;
  created_by: string;
  created_at: string;
  is_active: boolean;
  medias: Array<{
    media_uuid: string;
    media_type: string;
    mime_type: string;
    thumbnail: string;
    original: string;
  }>;
};

export type ServiceRequestIssue = {
  issue_id: string;
  title: string;
  description?: string;
  work_orders?: Array<{
    work_order_id: string;
    work_order_number: string;
  }>;
  problem?: {
    category?: {
      category_id: string;
      label: string;
    };
    sub_category?: {
      sub_category_id: string;
      label: string;
    };
    diagnosis?: {
      diagnosis_id: string;
      label: string;
    };
  };
  status?: {
    value: string;
    label: string;
  };
  abilities?: Array<string>;
  created_at?: string;
};

/**
 * A work order as it is held inside a service request's `work_orders` list.
 *
 * `issues`, `can_assign`, `can_unassign` and `work_order_ai_summary` are OPTIONAL
 * on purpose: all four are ABSENT from the realtime `serviceRequest.workOrder.created`
 * payload (ORI-571). That frame is built by the BE
 * `BuildsSchedulingRealtimePayload::schedulingAwareWorkOrderCreatedPayload` and
 * carries only 15 scheduling keys - no `issues`, no `abilities`, no `can_assign`,
 * no `can_unassign` and no `work_order_ai_summary` - so a work order created while
 * the slide-out is open enters the store without them.
 *
 * `can_assign` gates assigning a NEW issue here; `can_unassign` gates removing one
 * already assigned. They are not interchangeable - they diverge on Canceled.
 *
 * Writers of `issues`:
 *  1. the SR-details GET - BE `WorkOrderResource` -> `WorkOrderIssuesResource`,
 *     which emits `{ issue_id, title }`;
 *  2. `updateIssueInServiceRequestWorkOrders` (`add` / `delete` / `unAssign`);
 *  3. the two `serviceRequest.workOrder.updated` issue subclasses
 *     (issue-updated / issue-assigned), which send `{ issue_id, title, status }`.
 *
 * Readers of `issues`:
 *  1. `SRWorkOrderCardV2` reads `issue_id` ONLY and resolves each title from
 *     `state.issues.issuesSource` - a two-slice join;
 *  2. `useServiceRequestActions` reads `issue.title` DIRECTLY when it builds the
 *     Delete-Work-Order confirmation modal, so a title-less entry renders blank.
 */
export type WorkOrderWithAdditionalData = WorkOrderDataObj & {
  issues?: ServiceRequestIssue[];
  can_assign?: boolean;
  can_unassign?: boolean;
  work_order_ai_summary?: string;
};

/**
 * Shape of a work order as it arrives over Pusher. Every realtime producer sends a
 * PARTIAL work order keyed by `work_order_id`, never the full resource, so realtime
 * reducers must be annotated with this type rather than
 * `WorkOrderWithAdditionalData` (ORI-571).
 *
 * The three payload shapes currently on the wire, by key name only:
 *  - `serviceRequest.workOrder.created` (15 keys): `work_order_id`,
 *    `work_order_number`, `status`, `is_pending_scheduling`, `priority`,
 *    `estimated_duration_minutes`, `locked`, `job_type`, `job_classification`,
 *    `created_at`, `location`, `resident_availability_windows`,
 *    `service_request_uuid`, `resident_availability_request_pending`,
 *    `resident_availability_requested_at`;
 *  - `serviceRequest.workOrder.updated` base event: `work_order_id`, `status`,
 *    `abilities`, `work_order_issues`, `work_order_ai_summary`;
 *  - its issue-updated / issue-assigned subclasses: `work_order_id`, `issues`,
 *    `abilities`.
 */
export type RealtimeWorkOrderPayload = Partial<WorkOrderWithAdditionalData> & {
  work_order_id: string;
};

/**
 * Service request
 */
export type ServiceRequestDataObj = {
  service_request_id: string;
  service_request_number: string;
  description: string;
  original_description?: string | null;
  cleaned_description?: string | null;
  resident_id: string;
  property: ServiceRequestProperty;
  type: ServiceRequestType;
  assignees: Array<AssigneeDetails>;
  status: StatusType;
  imported_from: ServiceRequestImportedFrom;
  priority: ServiceRequestPriority;
  timezone: string;
  abilities: Array<string>;
  categories: Array<ServiceRequestCategory>;
  issues: Array<string>;
  issuesData?: Array<ServiceRequestIssue>;
  resident_availabilities: {
    availability: Array<ResidentAvailabilityResponseType>;
    requested_at: string;
    display_availability_added_alert: boolean;
    availability_created_at: string;
    resident_added_availability_count: string;
    resident_availability_source?: NormalizedResidentAvailabilityType;
    available_dates?: string[];
    resident_availability?: ResidentAvailabilityResponseType[];
  };
  description_history: Array<DescriptionHistoryType>;
  group: string;
  media: Array<MediaType>;
  /**
   * Raw created-at as the SR DETAILS endpoint sends it. Bridged onto `added_date`
   * by the details extra-reducer; no view reads this field directly.
   */
  date_added?: string;
  /**
   * Canonical created-at for the UI: sent raw by the SR LIST endpoint, and remapped
   * from `date_added` on details. Read by the SR Listing "Added Date" column and the
   * Basic Info "Created At" row.
   */
  added_date?: string;
  work_orders: Array<WorkOrderWithAdditionalData>;
  description_updated_by: string;
  description_updated_at: string;
  work_order_count: number;
  /** SR-level invoice-cost rollup (ORI-576 / ORI-556 AC3). ONE BE field per surface —
   *  FE never sums work orders. Mirrors the coordinator WO fields from PR #1083. */
  total_invoice_amount_in_cents?: number | null;
  has_invoiced_cost?: boolean;
  nonEditableFields: (
    | 'address'
    | 'priority'
    | 'resident_info'
    | 'nte'
    | 'media'
    | 'issues'
    | 'resident_availability'
  )[];
  market?: {
    id: string;
    name: string;
  };
  out_of_scope_for_viewer?: boolean;
  property_insights?: Array<{
    title: string;
    slug: string;
    description: string;
    is_property_level?: boolean;
    issue_id: string;
    reference_type: 'work_order_no' | 'service_request_no';
    reference_id: Record<string, string>;
  }>;
  rejection?: {
    rejected_at: string;
    rejected_by: string;
    internal_reason: string;
    resident_notified: boolean;
    message_to_resident?: string;
  };
  /**
   * Count of unread resident-chat messages on this service request, viewed
   * from the current coordinator's perspective. Backs the SR list's chat
   * unread badge; optional so rows from an older API response don't break.
   */
  unread_messages_count?: number;
};

export interface ServiceRequestAPIResponseObj extends ServiceRequestDataObj {
  tags: Array<TagData>;
}

/**
 * Service request pagination metadata
 */
export type ServiceRequestMetaDataObj = {
  current_page: number;
  from: number;
  last: string;
  next: string;
  per_page: number;
  prev: string;
  to: number;
  total: number;
};

/**
 * Service request details
 */
export type SrRightSection = 'activityLog' | 'workOrders' | 'chat' | 'chatDetails' | 'property';
export type SrLeftSection = 'srDetails';
export type StateStatusActions =
  | 'awaitingAvailabilityServiceRequest'
  | 'inProgressServiceRequest'
  | 'closedServiceRequest'
  | 'createWorkOrderServiceRequest';

/**
 * Service request inital state type
 */
export type InitialStateType = {
  data: {
    data: string[];
    meta: ServiceRequestMetaDataObj;
  };
  source: {
    [key: string]: ServiceRequestDataObj;
  };
  groupedData: {
    [key: string]: Array<string>;
  };
  groupedDataMeta: {
    [key: string]: GroupedDataMetaItem;
  };
  showSidePanel: {
    needToRefetch?: boolean;
    isOpen?: boolean;
    serviceRequestUuid?: string;
    leftSection?: SrLeftSection | null;
    rightSection?: SrRightSection | null;
    mobileSection?: SrRightSection | null;
    issuesToAssign?: Array<string>;
    hideAssignedIssue?: boolean;
  } | null;
  currentFilterReferenceObj: { [key: string]: any };
  /**
   * Single-SR resident-availability requests this client fired and that have not
   * settled yet, keyed by service request id. The 202 carries no timestamp (APX-521),
   * so the banner shows a client clock until the queued job resolves; `previousRequestedAt`
   * is what that optimistic stamp replaced, so a failed send can be undone instead of
   * being left showing a success that never happened (APX-607).
   */
  pendingAvailabilityRequests: {
    [serviceRequestId: string]: { previousRequestedAt: string };
  };
  currentWorkOrderStep: {
    step: string;
    serviceRequestId?: string;
    workOrderId?: string;
  } | null;
  isLoading: boolean;
  isUpdatingServiceRequestDetails: boolean;
  isFetchingServiceRequestDetails: boolean;
  hasServiceRequestDetailsError: boolean;
  isRequestingResidentAvailability: boolean;
  isUpdatingResidentAvailability: boolean;
  isAddingResidentAvailability: boolean;
  isManualAddingResidentAvailability: boolean;
  isSubmittingWorkOrder: boolean;
  isDeletingWorkOrder: boolean;
  isDisableAddingAvailability: boolean;
  isRejectingServiceRequest: boolean;
};

/**
 * Service request inital state value
 */
export const serviceRequestInitialState: InitialStateType = {
  data: {
    data: [],
    meta: {
      current_page: 0,
      from: 0,
      last: '',
      next: '',
      per_page: 0,
      prev: '',
      to: 0,
      total: 0,
    },
  },
  source: {},
  groupedData: {},
  groupedDataMeta: {},
  showSidePanel: null,
  currentFilterReferenceObj: {},
  pendingAvailabilityRequests: {},
  currentWorkOrderStep: null,
  isLoading: false,
  isUpdatingServiceRequestDetails: false,
  isFetchingServiceRequestDetails: true,
  hasServiceRequestDetailsError: false,
  isUpdatingResidentAvailability: false,
  isAddingResidentAvailability: false,
  isManualAddingResidentAvailability: false,
  isRequestingResidentAvailability: false,
  isSubmittingWorkOrder: false,
  isDeletingWorkOrder: false,
  isDisableAddingAvailability: false,
  isRejectingServiceRequest: false,
};
